import React from 'react';

const QuestionnairePage = ({
  question,
  currentIndex,
  totalQuestions,
  response,
  onResponse,
  onNext,
  onPrevious,
  onSaveAndContinue,
  completedCategories,
  allCategories,
  userInfo,
  loading
}) => {
  const handleOptionSelect = (optionValue) => {
    onResponse(question, optionValue);
  };

  const getOptionValue = (optionText) => {
    // Extract the numeric value from option text like "(1) No formal strategy..."
    const match = optionText.match(/^\((\d+)\)/);
    return match ? parseInt(match[1]) : 1;
  };

  const isLastQuestion = currentIndex === totalQuestions - 1;
  const canProceed = response !== undefined;

  return (
    <div className="min-h-screen p-4">
      {/* Header with Logo and Progress */}
      <div className="max-w-4xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <img 
            src="/logos/DP LOGO - ON WHT.png" 
            alt="Dewpoint Group Logo" 
            className="h-12 w-auto"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
          <div className="text-right">
            <p className="text-sm text-gray-600">
              Question {currentIndex + 1} of {totalQuestions}
            </p>
            <p className="text-xs text-gray-500">
              {userInfo?.userName} - {userInfo?.companyName}
            </p>
          </div>
        </div>

        {/* Category Progress Bar */}
        <div className="mb-8">
          <h3 className="text-sm font-medium text-gray-700 mb-3">Progress by Category:</h3>
          <div className="flex flex-wrap gap-2 max-w-full">
            {allCategories.map((category) => {
              // Shorten category names for better display
              const shortCategory = category
                .replace('AI Use Case Portfolio', 'AI Use Cases')
                .replace('MLOps & Deployment', 'MLOps')
                .replace('Security & Compliance', 'Security')
                .replace('Leadership & Vision', 'Leadership')
                .replace('AI Talent & Skills', 'AI Talent')
                .replace('Data Infrastructure', 'Infrastructure')
                .replace('Data Governance', 'Governance')
                .replace('Model Development', 'Models')
                .replace('Risk & Ethics', 'Ethics')
                .replace('Change Management', 'Change Mgmt')
                .replace('Customer Impact', 'Customer')
                .replace('Financial Investment', 'Investment');
              
              return (
                <span
                  key={category}
                  className={`px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                    completedCategories.includes(category)
                      ? 'bg-blue-600 text-white'
                      : category === question.category
                      ? 'bg-orange-100 text-orange-800'
                      : 'bg-gray-200 text-gray-600'
                  }`}
                  title={category} // Show full name on hover
                >
                  {shortCategory}
                </span>
              );
            })}
          </div>
        </div>

        {/* Question Card */}
        <div className="glass-card rounded-2xl p-8 mb-6">
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <span className="bg-blue-100 text-blue-800 text-sm font-medium px-3 py-1 rounded-full">
                {question.category}
              </span>
              <span className="text-sm text-gray-500">
                Remaining: {totalQuestions - currentIndex - 1}
              </span>
            </div>
            
            <h2 className="text-xl font-semibold text-gray-800 leading-relaxed">
              {question.question}
            </h2>
          </div>

          {/* Response Options */}
          <div className="space-y-3">
            {question.responseOptions.map((option, index) => {
              const optionKey = Object.keys(option)[0];
              const optionText = option[optionKey];
              const optionValue = getOptionValue(optionText);
              const isSelected = response === optionValue;

              return (
                <button
                  key={index}
                  onClick={() => handleOptionSelect(optionValue)}
                  className={`w-full text-left p-4 rounded-lg border-2 transition-all duration-200 ${
                    isSelected
                      ? 'border-blue-500 bg-blue-50 text-blue-900'
                      : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-start">
                    <div className={`w-5 h-5 rounded-full border-2 mr-3 mt-0.5 flex-shrink-0 ${
                      isSelected
                        ? 'border-blue-500 bg-blue-500'
                        : 'border-gray-300'
                    }`}>
                      {isSelected && (
                        <div className="w-full h-full rounded-full bg-white scale-50"></div>
                      )}
                    </div>
                    <span className="text-sm leading-relaxed">{optionText}</span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <button
            onClick={onPrevious}
            disabled={currentIndex === 0 || loading}
            className="px-6 py-3 text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
          >
            ← Previous
          </button>

          <div className="flex flex-col items-center">
            <div className="w-64 bg-gray-200 rounded-full h-2 mb-2">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${((currentIndex + 1) / totalQuestions) * 100}%` }}
              ></div>
            </div>
            <span className="text-xs text-gray-500" style={{ marginBottom: '50px' }}>
              {Math.round(((currentIndex + 1) / totalQuestions) * 100)}% Complete
            </span>
            
            {/* Save & Continue Button - Only show if NOT the last question */}
            {!isLastQuestion && (
              <button
                onClick={onSaveAndContinue}
                disabled={loading}
                className="px-6 py-3 bg-green-600 text-white hover:bg-green-700 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                {loading ? 'Saving...' : 'Save & Continue'}
              </button>
            )}
          </div>

          <button
            onClick={onNext}
            disabled={!canProceed || loading}
            className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLastQuestion ? 'Complete Assessment' : 'Next →'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuestionnairePage;